import { useCallback, useMemo, useState } from 'react'
import { Alert, Button, DatePicker, Form, Select, Space, Tag, Tooltip, Typography } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import dayjs, { type Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePlanOptions } from '@/shared/api/reference'
import { displayName } from '@/shared/lib/planName'
import { rangeToIso } from '@/shared/lib/dayRange'
import { formatDateTime } from '@/shared/lib/format'
import { formatBytes } from '@/shared/lib/format.units'
import { shortId } from '@/shared/lib/shortId'
import { usePermissions } from '@/shared/auth/usePermissions'
import { DataTable } from '@/shared/ui/table/DataTable'
import { RowActions } from '@/shared/ui/table/RowActions'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import { PageHeader } from '@/shared/ui/PageHeader'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import { AgentRef } from '@/features/agent/AgentRef'
import { AgentSelect } from '@/features/agent/AgentSelect'
import { fetchSubscriber } from '@/features/subscriber/subscriberApi'
import {
  FUNDED_BY,
  GRANT_LIST_STATES,
  GRANT_SOURCES,
  fetchGrantPage,
  type GrantListState,
  type GrantRow,
} from '@/features/grant/grantApi'
import { subscriberTransactionsHref } from '@/features/grant/subscriberLinks'
import { SubscriberSelect } from '@/features/subscriber/SubscriberSelect'

type Filters = TableState['filters']

const STATE_COLOR: Readonly<Record<GrantListState, string>> = {
  PENDING: 'default',
  ACTIVE: 'green',
  EXHAUSTED: 'gold',
  EXPIRED: 'default',
  REVOKED: 'red',
}

function single(values: readonly string[] | undefined): string | null {
  return values?.[0] ?? null
}

function known<T extends string>(value: string | null, allowed: readonly T[]): T | null {
  return allowed.find((item) => item === value) ?? null
}

/**
 * Berilgan paketlar ro'yxati.
 *
 * <h2>Nega alohida sahifa</h2>
 * Egasi talabi (SHARTNOMA-2 §B): grantlar shu paytgacha faqat abonent
 * kartochkasida, bitta abonent doirasida ko'rinardi. «Agent nechta paket
 * sotdi», «bugun qaysi paketlar berildi» degan savollarga javob
 * beradigan joy yo'q edi.
 *
 * <h2>⚠️ MUDDATI O'TGAN PAKET — TARIX, YOPILGAN EMAS</h2>
 * ADR-033 bu ro'yxatga QO'LLANMAYDI va sabab uning O'Z istisnolar
 * jadvalida yozilgan: «Vaucher partiyasi — muddati o'tgani tarix,
 * yopilgan emas». Grant ham reyestr yozuvi emas, sodir bo'lgan voqea:
 * `EXPIRED`/`EXHAUSTED`/`REVOKED` ni sukut bo'yicha yashirish bir yillik
 * abonentda ro'yxatni bo'shatib qo'yardi va operator uni «paket
 * olmagan» deb o'qirdi. Batafsil asos — `grantApi.GRANT_LIST_STATES`.
 * Qoidaning o'zagi (yagona yo'l — holat filtri) esa qo'llanadi.
 *
 * <h2>⚠️ SAHIFALASH SERVERDA</h2>
 * `grantListParams` har so'rovda `page`/`size` yuboradi; filtr, qidiruv
 * va saralash ham serverga boradi. Mijozda kesish TAQIQ
 * (JADVAL-QOIDASI §6).
 *
 * <h2>⚠️ AVTOYANGILANISH YO'Q</h2>
 * `refetchInterval` ATAYLAB berilmagan (egasi qarori): bu sahifa jonli
 * holat emas, tarix ko'rsatadi.
 */
export function GrantsPage() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { can } = usePermissions()
  const { state, patch } = useTableUrlState()

  /**
   * ⚠️ Jami son ANIQ ekani JAVOBDAN ma'lum bo'ladi, `showTotal` esa
   * undan oldin beriladi. Shartnomada `total` yoki `hasNext` — hali
   * tanlanmagan, shuning uchun sahifa ikkalasiga ham tayyor: aniq son
   * kelmasa «1–25 / N» UMUMAN chizilmaydi.
   */
  const [totalKnown, setTotalKnown] = useState(false)
  const onShape = useCallback((value: boolean) => setTotalKnown(value), [])

  const subscriberId = single(state.filters['subscriberId'])
  const agentId = single(state.filters['agentId'])
  const planId = single(state.filters['planId'])
  const source = known(single(state.filters['source']), GRANT_SOURCES)
  const grantState = known(single(state.filters['state']), GRANT_LIST_STATES)
  const from = single(state.filters['from'])
  const to = single(state.filters['to'])

  const plans = usePlanOptions()

  /**
   * ⚠️ ABONENT NOMI ALOHIDA SO'ROV bilan olinadi va bu ataylab:
   * qatorlardagi `subscriberLogin` ro'yxat BO'SH bo'lganda yo'q, aynan
   * o'sha holatda esa operatorga «qaysi abonent» degan javob eng kerak.
   */
  const subscriber = useQuery({
    queryKey: queryKeys.subscribers.detail(subscriberId ?? ''),
    queryFn: () => fetchSubscriber(subscriberId ?? ''),
    enabled: subscriberId !== null,
    staleTime: 60_000,
    retry: false,
  })

  const commit = (next: Filters) => patch({ page: 1, filters: next })

  const clearSubscriber = () => {
    const next = { ...state.filters }
    delete next['subscriberId']
    commit(next)
  }

  // ⚠️ `subscriberId` ham SANALADI: u endi panel ichidagi maydon.
  // Sanalmasa operator panelni ochib to'ldirilgan filtrni ko'rardi, nishon
  // esa «filtr yo'q» deb turardi — jimgina qarama-qarshilik.
  const drawerCount = [subscriberId, agentId, planId, source, from].filter(
    (value) => value !== null,
  ).length

  const range: [Dayjs, Dayjs] | null = from && to ? [dayjs(from), dayjs(to)] : null

  /**
   * ⚠️ Nom TOPILMASA XOM ID ko'rsatiladi, «noma'lum» DEYILMAYDI: yozuv
   * bor, uni o'qishga huquq yoki katalog yozuvi yetishmayapti — bu ikki
   * boshqa narsa.
   */
  const planLabel = useMemo(() => {
    const index = new Map(plans.map((plan) => [plan.id, displayName(plan, i18n.language)]))
    return (row: GrantRow): string => {
      if (row.planName) return row.planName
      if (row.planId === null) return t('common.dash')
      return index.get(row.planId) ?? shortId(row.planId)
    }
  }, [plans, i18n.language, t])

  const columns: ColumnsType<GrantRow> = [
    {
      key: 'subscriber',
      title: t('grants.columns.subscriber'),
      width: 180,
      render: (_value, row) => (
        <Link to={`/subscribers/${row.subscriberId}`}>
          {row.subscriberLogin ?? shortId(row.subscriberId)}
        </Link>
      ),
    },
    {
      key: 'plan',
      title: t('grants.columns.plan'),
      width: 200,
      render: (_value, row) => planLabel(row),
    },
    {
      key: 'state',
      title: t('grants.columns.state'),
      width: 130,
      render: (_value, row) => {
        const value = known(row.state, GRANT_LIST_STATES)
        if (value === null) {
          // Tanilmagan holat XOM ko'rinadi — `t()` ga berilsa ekranda
          // "grantState.XYZ" degan texnik kalit turardi
          return row.state ?? t('common.dash')
        }
        return <Tag color={STATE_COLOR[value]}>{t(`grantState.${value}`)}</Tag>
      },
    },
    {
      key: 'source',
      title: t('grants.columns.source'),
      width: 150,
      render: (_value, row) => {
        const value = known(row.source, GRANT_SOURCES)
        return value === null ? (row.source ?? t('common.dash')) : t(`grantSource.${value}`)
      },
    },
    {
      key: 'fundedBy',
      title: t('grants.columns.fundedBy'),
      width: 180,
      render: (_value, row) => {
        /*
          ⛔ VAUCHER QATORIDA SERVER YOLG'ON QIYMAT YUBORADI.

          Jonli Staging (2026-08-24): `nurbek1999` · `5 GB` · Manba
          «Vaucher» · To'lagan tomon «Abonent balansi». Bu NOTO'G'RI —
          karta aktivatsiyasida balansdan hech narsa yechilmaydi:
          `VoucherServiceImpl.activate` grantni `charge = false` bilan
          chaqiradi, `GrantServiceImpl.postSale` esa pulsiz grant uchun
          ledgerga UMUMAN murojaat qilmaydi. Vaucher bugun pul
          jurnalida yo'q (`VoucherRepository` da ochiq yozilgan).

          ⚠️ ASL SABAB PANELDA EMAS: `GrantRequest` ga
          `FundedBy.SUBSCRIBER` uzatiladi va panel uni halol chizadi.
          To'g'ri qiymat `FundedBy.NONE` bo'lardi, LEKIN uni backendda
          o'zgartirish ledger va hisobotga tegadi — bu alohida qaror.
          Shu bilan birga ekranda YOLG'ON qoldirib bo'lmasdi: operator
          «abonent pul to'lagan» deb o'qiydi va kassani izlay
          boshlaydi.

          ⚠️ Shart MANBA bo'yicha, `fundedBy` bo'yicha emas: backend
          qiymatni tuzatgan kunda ham bu shox to'g'ri qoladi.
        */
        if (known(row.source, GRANT_SOURCES) === 'VOUCHER') {
          return (
            <Tooltip title={t('grants.fundedByVoucherHint')}>
              <Tag>{t('grants.fundedByVoucher')}</Tag>
            </Tooltip>
          )
        }
        const value = known(row.fundedBy, FUNDED_BY)
        return value === null ? (row.fundedBy ?? t('common.dash')) : t(`fundedBy.${value}`)
      },
    },
    {
      key: 'agent',
      title: t('grants.columns.agent'),
      width: 200,
      /*
        ⚠️ UCH HOLAT ATAYLAB AJRATILGAN:
          · agent YO'Q          → chiziqcha. Bu NORMAL: operator
            to'g'ridan-to'g'ri sotgan bo'lishi mumkin, va `access_grant`
            ustuni yaqinda qo'shilgani uchun ESKI qatorlarda u bo'sh.
            «Noma'lum agent» deb yozish yo'q ma'lumotni bor qilib
            ko'rsatardi;
          · nom bor            → nom (server bitta so'rov bilan beradi);
          · id bor, nom yo'q   → `AgentRef` — u huquqsizlikni, topilmagan
            yozuvni va nomni o'zi ajratadi.
      */
      render: (_value, row) => {
        if (row.grantedByAgentId === null) {
          return <Typography.Text type="secondary">{t('common.dash')}</Typography.Text>
        }
        if (row.grantedByAgentName) return row.grantedByAgentName
        return <AgentRef agentId={row.grantedByAgentId} showCode={false} />
      },
    },
    {
      key: 'startsAt',
      title: t('grants.columns.startsAt'),
      width: 180,
      sorter: true,
      render: (_value, row) => (row.startsAt ? formatDateTime(row.startsAt) : t('common.dash')),
    },
    {
      key: 'expiresAt',
      title: t('grants.columns.expiresAt'),
      width: 180,
      sorter: true,
      // `null` = muddatsiz. "—" bilan aralashtirilmaydi: birinchisi
      // "cheksiz", ikkinchisi "ma'lumot yo'q" degani bo'lardi
      render: (_value, row) =>
        row.expiresAt ? formatDateTime(row.expiresAt) : t('limits.noExpiration'),
    },
    {
      key: 'quota',
      title: t('grants.columns.quota'),
      width: 140,
      align: 'right',
      render: (_value, row) =>
        row.quotaBytes === null ? t('grants.unlimitedQuota') : formatBytes(row.quotaBytes),
    },
    {
      key: 'actions',
      title: '',
      width: 56,
      align: 'center',
      fixed: 'right',
      render: (_value, row) => (
        <RowActions
          items={[
            {
              key: 'card',
              label: t('grants.actions.openSubscriber'),
              onClick: () => void navigate(`/subscribers/${row.subscriberId}`),
            },
            {
              key: 'onlyThis',
              label: t('grants.actions.onlyThisSubscriber'),
              hidden: subscriberId === row.subscriberId,
              onClick: () => commit({ ...state.filters, subscriberId: [row.subscriberId] }),
            },
            {
              key: 'payments',
              label: t('subscriber.links.transactions'),
              permission: 'ledger.read',
              onClick: () => void navigate(subscriberTransactionsHref(row.subscriberId)),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <>
      <PageHeader title={t('nav.grants')} description={t('pageIntro.grants')} />

      {/*
        ⚠️ ABONENT FILTRI KO'RINADIGAN BO'LISHI SHART.

        Havola bilan kelganda ro'yxat bitta abonentga toraygan bo'ladi.
        Buni aytmasdan qoldirish jimgina yolg'on: operator qisqargan
        ro'yxatni «bugun kam paket sotilibdi» deb o'qirdi. Shuning uchun
        LOGIN bilan yoziladi va yonida BIR BOSISHLIK tozalash turadi.
      */}
      {subscriberId !== null ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={t('grants.subscriberFilter', {
            subscriber: subscriber.isPending
              ? t('common.loading')
              : (subscriber.data?.login ?? shortId(subscriberId)),
          })}
          description={t('grants.subscriberFilterHint')}
          action={
            <Space direction="vertical" size={4}>
              <Button size="small" onClick={clearSubscriber}>
                {t('grants.clearSubscriber')}
              </Button>
              <Link to={`/subscribers/${subscriberId}`}>{t('grants.openSubscriber')}</Link>
            </Space>
          }
        />
      ) : null}

      <DataTable<GrantRow>
        queryKey={queryKeys.grants.list}
        fetchPage={fetchGrantPage(onShape)}
        columns={columns}
        rowKey={(row) => row.id}
        tableId="grants"
        stickyFirstColumn
        scrollX={1500}
        searchable
        searchPlaceholder={t('grants.searchPlaceholder')}
        // ⚠️ Jami son SERVER aytmasa CHIZILMAYDI — taxminni haqiqat qilib
        //    ko'rsatishdan ko'ra ko'rsatmaslik afzal (SHARTNOMA-2 §B).
        showTotal={totalKnown}
        emptyTitle={t('grants.empty')}
        emptyDescription={t('grants.emptyHint')}
        toolbarExtra={
          <Space wrap>
            {/* Holat TASHQARIDA: «hozir nechta faol paket bor» — bu
                sahifadagi eng ko'p beriladigan savol */}
            <Select<GrantListState | null>
              allowClear
              placeholder={t('grants.columns.state')}
              value={grantState}
              onChange={(value) => commit({ ...state.filters, state: value ? [value] : [] })}
              style={{ width: 170 }}
              options={GRANT_LIST_STATES.map((value) => ({
                value,
                label: t(`grantState.${value}`),
              }))}
            />
            <FilterDrawer<Filters>
              value={state.filters}
              resetValue={{
                ...state.filters,
                subscriberId: [],
                agentId: [],
                planId: [],
                source: [],
                from: [],
                to: [],
              }}
              activeCount={drawerCount}
              onApply={commit}
            >
              {(draft, patchDraft) => (
                <>
                  {/*
                    ⚠️ ABONENT FILTRI ENDI PANELDA HAM.

                    Ilgari uni qo'yishning YAGONA yo'li qatordan «faqat
                    shu abonent» ni bosish edi — ya'ni operator avval
                    kerakli abonentning grantini TOPISHI kerak edi.
                    Ro'yxat bo'sh bo'lsa (masalan davr boshqa) bosadigan
                    qator ham yo'q va filtrga umuman yetib bo'lmasdi.

                    ⚠️ Tanlagich LOGIN bo'yicha — UUID panelda hech
                    qayerda ko'rinmaydi.
                  */}
                  {can('subscriber.read') ? (
                    <Form.Item label={t('grants.columns.subscriber')}>
                      <SubscriberSelect
                        value={single(draft['subscriberId'])}
                        onChange={(value) =>
                          patchDraft({ subscriberId: value ? [value] : [] })
                        }
                      />
                    </Form.Item>
                  ) : null}

                  {/* ⚠️ `kinds` BERILMAYDI: shartnomada `agentId` «agent
                      YOKI reseller — bittasi». Cheklov qo'yilsa
                      ikkinchisi ro'yxatdan yo'qolardi. */}
                  {can('agent.read') ? (
                    <Form.Item label={t('grants.columns.agent')}>
                      <AgentSelect
                        value={single(draft['agentId'])}
                        onChange={(value) => patchDraft({ agentId: value ? [value] : [] })}
                        placeholder={t('grants.agentPlaceholder')}
                      />
                    </Form.Item>
                  ) : null}

                  <Form.Item label={t('grants.columns.plan')}>
                    <Select<string | null>
                      allowClear
                      showSearch
                      optionFilterProp="label"
                      value={single(draft['planId'])}
                      onChange={(value) => patchDraft({ planId: value ? [value] : [] })}
                      placeholder={t('grants.planPlaceholder')}
                      style={{ width: '100%' }}
                      /* ⚠️ `sellable` bo'yicha KESILMAYDI: ro'yxat tarix,
                         va sotuvdan olingan paket o'tmishda berilgan
                         bo'lishi mumkin. Kesilsa o'sha grantlarni
                         filtrlab bo'lmasdi. */
                      options={plans.map((plan) => ({
                        value: plan.id,
                        label: displayName(plan, i18n.language),
                      }))}
                    />
                  </Form.Item>

                  <Form.Item label={t('grants.columns.source')}>
                    <Select<string | null>
                      allowClear
                      value={single(draft['source'])}
                      onChange={(value) => patchDraft({ source: value ? [value] : [] })}
                      placeholder={t('grants.sourcePlaceholder')}
                      style={{ width: '100%' }}
                      options={GRANT_SOURCES.map((value) => ({
                        value,
                        label: t(`grantSource.${value}`),
                      }))}
                    />
                  </Form.Item>

                  <Form.Item label={t('grants.rangeLabel')}>
                    {/* ⚠️ Oyna TENANT zonasida onga aylantiriladi
                        (`rangeToIso`) — brauzer zonasi tenantnikidan
                        farq qilganda oxirgi kun jimgina tushib qolardi */}
                    <DatePicker.RangePicker
                      allowClear
                      style={{ width: '100%' }}
                      value={
                        single(draft['from']) && single(draft['to'])
                          ? [dayjs(single(draft['from'])), dayjs(single(draft['to']))]
                          : null
                      }
                      onChange={(value) => {
                        const picked = value && value[0] && value[1] ? rangeToIso([value[0], value[1]]) : null
                        patchDraft({
                          from: picked ? [picked.from] : [],
                          to: picked ? [picked.to] : [],
                        })
                      }}
                    />
                  </Form.Item>
                </>
              )}
            </FilterDrawer>
          </Space>
        }
      />

      {range !== null ? (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('grants.rangeNote', {
            from: formatDateTime(range[0].toISOString()),
            to: formatDateTime(range[1].toISOString()),
          })}
        </Typography.Text>
      ) : null}
    </>
  )
}
